import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import AccountsSubNav from '../components/AccountsSubNav'
import AppActionMenu from '../../../../components/AppActionMenu'
import AppPagination from '../../../../components/AppPagination'
import SharedFilters from '../../../../components/SharedFilters'
import { appToast } from '../../../../components/AppToast'
import { useAcHeadListScreen } from './hooks/useAcHeadListScreen'

function formatCurrency(amount) {
  const num = Number(amount || 0)
  return num.toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
}

function getInitials(name) {
  const clean = String(name || '').trim()
  if (!clean) return 'NA'
  return clean.slice(0, 2).toUpperCase()
}

export default function AcHeadListScreen() {
  const navigate = useNavigate()
  const { state, actions } = useAcHeadListScreen()

  useEffect(() => {
    if (state.error) appToast.error(state.error)
  }, [state.error])

  useEffect(() => {
    if (state.success) appToast.success(state.success)
  }, [state.success])

  const companyOptions = [
    { value: 'All', label: 'All Companies' },
    { value: '1', label: 'Bridge Global Holdings' },
    { value: '2', label: 'Bridge Pacific Ventures' },
  ]

  const branchOptions = [
    { value: 'All', label: 'All Branches' },
    { value: '1', label: 'London Central' },
    { value: '2', label: 'Singapore Hub' },
  ]

  const departmentOptions = [
    { value: 'All', label: 'All Departments' },
    { value: '1', label: 'Engineering' },
    { value: '2', label: 'Finance' },
  ]

  const placeOptions = [
    { value: 'All', label: 'All Places' },
    { value: 'London', label: 'London' },
    { value: 'Singapore', label: 'Singapore' },
    { value: 'Mumbai', label: 'Mumbai' },
  ]

  const groupOptions = [
    { value: 'All', label: 'All Groups' },
    ...state.groups.map((g) => ({ value: g.name, label: g.name })),
  ]

  const headTypeOptions = [
    { value: 'All', label: 'Show all A/c heads' },
    { value: 'Debtors', label: 'Debtors' },
    { value: 'Creditors', label: 'Creditors' },
    { value: 'Assets', label: 'Assets' },
    { value: 'Liabilities', label: 'Liabilities' },
    { value: 'Income', label: 'Income' },
    { value: 'Expenses', label: 'Expenses' },
  ]

  const statusOptions = [
    { value: 'Active', label: 'Active' },
    { value: 'Archive', label: 'Archived' },
    { value: 'All', label: 'All' },
  ]

  const searchFieldOptions = [
    { value: 'Name', label: 'Name' },
    { value: 'Alias', label: 'Alias' },
    { value: 'Group', label: 'Group' },
    { value: 'Place', label: 'Place' },
  ]

  return (
    <div className="mx-auto flex w-full max-w-[1460px] flex-col gap-6 pb-12">
      {/* ── Header Title & Sub Navigation Tabs ── */}
      <section className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <p className="text-xs font-black uppercase tracking-[0.16em] text-primary">Financial Master</p>
          <h1 className="mt-1 font-display text-3xl font-extrabold tracking-tight text-on-surface">
            Account Heads
          </h1>
        </div>

        <AccountsSubNav />
      </section>

      {/* ── Collapsible Advanced Filters Accordion (Shared Component) ── */}
      <SharedFilters
        searchField={state.searchField}
        searchText={state.searchText}
        statusFilter={state.statusFilter}
        companyFilter={state.companyFilter}
        branchFilter={state.branchFilter}
        departmentFilter={state.departmentFilter}
        placeFilter={state.placeFilter}
        groupFilter={state.groupFilter}
        headTypeFilter={state.headTypeFilter}
        fromDate={state.fromDate}
        toDate={state.toDate}

        setSearchField={actions.setSearchField}
        setSearchText={actions.setSearchText}
        setStatusFilter={actions.setStatusFilter}
        setCompanyFilter={actions.setCompanyFilter}
        setBranchFilter={actions.setBranchFilter}
        setDepartmentFilter={actions.setDepartmentFilter}
        setPlaceFilter={actions.setPlaceFilter}
        setGroupFilter={actions.setGroupFilter}
        setHeadTypeFilter={actions.setHeadTypeFilter}
        setFromDate={actions.setFromDate}
        setToDate={actions.setToDate}
        onReset={actions.resetFilters}
        onSearch={() => {}}

        searchFieldOptions={searchFieldOptions}
        statusOptions={statusOptions}
        companyOptions={companyOptions}
        branchOptions={branchOptions}
        departmentOptions={departmentOptions}
        placeOptions={placeOptions}
        groupOptions={groupOptions}
        headTypeOptions={headTypeOptions}
        tools={[
          { key: 'print', label: 'Print', icon: 'print' },
          { key: 'download', label: 'Download (CSV / Excel)', icon: 'download' },
          { key: 'upload', label: 'Upload (Bulk Import)', icon: 'upload' },
          { key: 'configure', label: 'Update Columns', icon: 'settings' },
          { key: 'balance-diff', label: 'Opening Balance Difference Report', icon: 'analytics' }
        ]}
        onToolSelect={(key) => {
          if (key === 'download') {
            actions.triggerExport({
              viewMode: state.statusFilter,
              searchText: state.searchText,
              groupId: state.groupFilter !== 'All' ? state.groupFilter : undefined,
            })
          }
        }}
      />


      {/* ── Account Heads Data Table Card ── */}
      <section className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[850px] text-left text-sm">
            <thead>
              <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
                <th className="px-5 py-3.5">Name</th>
                <th className="w-36 px-5 py-3.5">Alias</th>
                <th className="px-5 py-3.5">Group</th>
                <th className="px-5 py-3.5 text-right">Amount</th>
                <th className="w-20 px-5 py-3.5 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/15">
              {state.isLoading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    Loading Account Heads...
                  </td>
                </tr>
              ) : state.items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-sm text-on-surface-variant font-medium">
                    No Account Heads found matching the filters.
                  </td>
                </tr>
              ) : (
                state.items.map((head) => {
                  const hasAmount = Number(head.amount || 0) > 0
                  const isCr = head.balance_type === 'Cr'
                  return (
                    <tr
                      key={head.id}
                      className="transition-colors hover:bg-surface-container-low/40"
                    >
                      {/* Name */}
                      <td className="px-5 py-3.5">
                        <div className="flex items-center gap-3">
                          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-xs font-black text-primary">
                            {getInitials(head.name)}
                          </div>
                          <div>
                            <p className="font-bold text-on-surface">{head.name}</p>
                            {head.place ? (
                              <p className="text-[11px] font-medium text-on-surface-variant">{head.place}</p>
                            ) : null}
                          </div>
                        </div>
                      </td>

                      {/* Alias */}
                      <td className="px-5 py-3.5 text-xs font-medium text-on-surface-variant">
                        {head.alias || '—'}
                      </td>

                      {/* Group */}
                      <td className="px-5 py-3.5">
                        <span className="inline-flex items-center rounded-lg bg-surface-container-low px-2.5 py-1 text-xs font-semibold text-secondary">
                          {head.group || '—'}
                        </span>
                      </td>

                      {/* Amount with Swapped Cr / Dr Colors & Enhanced Typography */}
                      <td className="px-5 py-3.5 text-right">
                        {hasAmount ? (
                          <div className="inline-flex items-center justify-end gap-2">
                            <span className="font-bold text-sm text-on-surface font-body tracking-tight tabular-nums">
                              ₹{formatCurrency(head.amount)}
                            </span>
                            <span
                              className={`rounded-md px-2 py-0.5 text-[11px] font-extrabold border ${
                                isCr
                                  ? 'bg-amber-100 text-amber-900 border-amber-300/60 shadow-xs'
                                  : 'bg-emerald-100 text-emerald-800 border-emerald-300/60 shadow-xs'
                              }`}
                            >
                              {head.balance_type || 'Dr'}
                            </span>
                          </div>
                        ) : (
                          <span className="text-on-surface-variant/60 text-sm font-medium">—</span>
                        )}
                      </td>

                      {/* Actions (Three-Dot AppActionMenu) */}
                      <td className="px-5 py-3.5 text-right">
                        <AppActionMenu
                          triggerLabel=""
                          triggerIcon="more_vert"
                          showChevron={false}
                          triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent"
                          items={[
                            { key: 'edit', label: 'Edit', icon: 'edit' },
                            { key: 'archive', label: head.status === 'Active' || head.is_active === 1 ? 'Archive' : 'Unarchive', icon: head.status === 'Active' || head.is_active === 1 ? 'archive' : 'unarchive' },
                            { key: 'delete', label: 'Delete', icon: 'delete' },
                            { key: 'analytics', label: 'Analytics', icon: 'trending_up' },
                            { key: 'unadjusted', label: 'Unadjusted', icon: 'adjust' },
                          ]}
                          onSelect={(key) => {
                            if (key === 'edit') {
                              navigate(`/masters/acheads/heads/entry?id=${head.id}`)
                            } else if (key === 'archive') {
                              actions.handleArchive(head.id)
                            } else if (key === 'delete') {
                              actions.handleDelete(head.id, head.name)
                            } else if (key === 'analytics') {
                              appToast.info(`Opening Analytics for account head "${head.name}"`)
                            } else if (key === 'unadjusted') {
                              appToast.info(`Opening Unadjusted ledger details for account head "${head.name}"`)
                            }
                          }}
                        />
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
        </div>

        {/* ── Table Footer & Pagination ── */}
        <div className="flex flex-col items-center justify-between gap-4 border-t border-outline-variant/15 px-6 py-4 sm:flex-row">
          <p className="text-xs text-on-surface-variant font-medium">
            Showing Page <span className="font-bold text-on-surface">{state.currentPage}</span> of{' '}
            <span className="font-bold text-on-surface">{state.totalPages}</span> ({state.allFilteredCount} total items)
          </p>

          <AppPagination
            page={state.currentPage}
            totalPages={state.totalPages}
            onPageChange={actions.setCurrentPage}
            onPrevious={() => actions.setCurrentPage((p) => Math.max(1, p - 1))}
            onNext={() => actions.setCurrentPage((p) => Math.min(state.totalPages, p + 1))}
            canPrevious={state.currentPage > 1}
            canNext={state.currentPage < state.totalPages}
          />
        </div>
      </section>
    </div>
  )
}

